/**
 * Foil compositor debug. Enable with ?foilDebug=1 (or localStorage foilDebug=1).
 * Optional ?foilBlend=off forces mix-blend-mode: normal so local vs prod can
 * be compared without GPU blending.
 */

export interface FoilLayerSnap {
  mixBlendMode: string
  opacity: string
  filter: string
  isolation: string
  transform: string
  backgroundImage: string
  boxShadow: string
}

export interface FoilAncestorSnap {
  tag: string
  className: string
  mixBlendMode: string
  isolation: string
  transform: string
  filter: string
  opacity: string
  perspective: string
  willChange: string
  overflow: string
  backdropFilter: string
}

export interface FoilDebugMeta {
  foilProp: string
  foilResolved: string | null
  foilReady: boolean
  alt: string
}

export interface FoilDebugReport {
  at: string
  origin: string
  href: string
  mode: string
  prod: boolean
  dpr: number
  colorScheme: string
  reducedMotion: boolean
  supportsPlusLighter: boolean
  supportsLighten: boolean
  foilProp: string
  foilResolved: string | null
  foilReady: boolean
  alt: string
  rootClass: string
  imgSrc: string
  imgCurrentSrc: string
  imgProtocol: string
  imgNatural: string
  imgComplete: boolean
  layers: Record<string, FoilLayerSnap | null>
  face: FoilLayerSnap & { overflow: string; transformStyle: string }
  root: { boxShadow: string; filter: string; transform: string }
  ancestors: FoilAncestorSnap[]
  cssHrefs: string[]
  styleTagCount: number
  autoDark: boolean
  darkReader: boolean
  htmlColorScheme: string
  metaColorScheme: string
  killers: string[]
}

type Listener = (report: FoilDebugReport) => void

let lastReport: FoilDebugReport | null = null
const listeners = new Set<Listener>()

function setFlag(el: HTMLElement, name: string, value: string | null): void {
  if (value == null) el.removeAttribute(name)
  else el.setAttribute(name, value)
}

/**
 * Ask Dark Reader to stand down. color-scheme alone does not stop it;
 * it rewrites linked production CSS and leaves Vite style tags alone.
 */
export function lockThirdPartyRecolor(): void {
  if (document.querySelector('meta[name="darkreader-lock"]')) return
  const lock = document.createElement('meta')
  lock.name = 'darkreader-lock'
  lock.content = 'true'
  document.head.appendChild(lock)
}

export function installFoilDebugFlags(): void {
  lockThirdPartyRecolor()
  if (import.meta.env.PROD) return
  const q = new URLSearchParams(location.search)
  const debug = q.get('foilDebug') === '1'
    || localStorage.getItem('foilDebug') === '1'
  const blendOff = q.get('foilBlend') === 'off'
    || localStorage.getItem('foilBlend') === 'off'
  const root = document.documentElement
  setFlag(root, 'data-foil-debug', debug ? '1' : null)
  setFlag(root, 'data-foil-blend', blendOff ? 'off' : null)
}

export function isFoilDebugEnabled(): boolean {
  return document.documentElement.dataset.foilDebug === '1'
}

export function isFoilBlendOff(): boolean {
  return document.documentElement.dataset.foilBlend === 'off'
}

export function setFoilBlendOff(off: boolean): void {
  setFlag(document.documentElement, 'data-foil-blend', off ? 'off' : null)
}

export function subscribeFoilDebug(fn: Listener): () => void {
  listeners.add(fn)
  if (lastReport) fn(lastReport)
  return () => {
    listeners.delete(fn)
  }
}

export function getLastFoilReport(): FoilDebugReport | null {
  return lastReport
}

export function publishFoilSnapshot(report: FoilDebugReport): void {
  lastReport = report
  listeners.forEach((fn) => fn(report))
  console.info('[foil-debug]', report)
}

function clip(value: string, max = 140): string {
  return value.length > max ? `${value.slice(0, max)}…` : value
}

function snapLayer(el: Element | null): FoilLayerSnap | null {
  if (!el) return null
  const s = getComputedStyle(el)
  return {
    mixBlendMode: s.mixBlendMode,
    opacity: s.opacity,
    filter: s.filter,
    isolation: s.isolation,
    transform: s.transform,
    backgroundImage: clip(s.backgroundImage, 160),
    boxShadow: clip(s.boxShadow, 120),
  }
}

function snapAncestor(el: Element): FoilAncestorSnap {
  const s = getComputedStyle(el)
  const cls = typeof el.className === 'string' ? el.className : ''
  return {
    tag: el.tagName.toLowerCase(),
    className: clip(cls.replace(/\s+/g, ' ').trim(), 80),
    mixBlendMode: s.mixBlendMode,
    isolation: s.isolation,
    transform: s.transform,
    filter: s.filter,
    opacity: s.opacity,
    perspective: s.perspective,
    willChange: s.willChange,
    overflow: `${s.overflowX}/${s.overflowY}`,
    backdropFilter: s.backdropFilter || (s as CSSStyleDeclaration & {
      webkitBackdropFilter?: string
    }).webkitBackdropFilter || 'none',
  }
}

function detectDarkReader(): boolean {
  const root = document.documentElement
  if (root.dataset.darkreaderMode || root.dataset.darkreaderScheme) return true
  return Boolean(document.querySelector('style.darkreader'))
}

function detectAutoDark(): boolean {
  const el = document.createElement('div')
  el.style.cssText = 'display:none;background-color:canvas;color-scheme:light'
  document.body.appendChild(el)
  const bg = getComputedStyle(el).backgroundColor
  el.remove()
  return bg !== 'rgb(255, 255, 255)'
}

function noteAncestorEffects(report: Omit<FoilDebugReport, 'killers'>): string[] {
  const out: string[] = []
  for (const a of report.ancestors) {
    const label = a.className ? `<${a.tag}.${a.className}>` : `<${a.tag}>`
    if (a.filter && a.filter !== 'none') {
      out.push(`ancestor filter on ${label}`)
    }
    if (a.backdropFilter && a.backdropFilter !== 'none') {
      out.push(`ancestor backdrop-filter on ${label}`)
    }
    if (a.opacity && a.opacity !== '1') {
      out.push(`ancestor opacity ${a.opacity} on ${label}`)
    }
  }
  return out
}

function collectKillers(report: Omit<FoilDebugReport, 'killers'>): string[] {
  const out: string[] = []
  if (report.imgProtocol === 'blob:') {
    out.push('img src is blob: (mix-blend often skips it)')
  }
  if (report.imgProtocol === 'data:') {
    out.push('img src is data:')
  }
  const wash = report.layers.wash
  if (wash?.mixBlendMode === 'normal') {
    out.push('wash mix-blend-mode is normal (CSS not applying)')
  }
  if (report.supportsPlusLighter && wash?.mixBlendMode === 'lighten') {
    out.push('@supports plus-lighter did not win; wash is lighten')
  }
  if (report.darkReader) {
    out.push('Dark Reader (or similar) is rewriting stylesheet colors')
  }
  if (report.autoDark) {
    out.push('Chrome Auto Dark is remapping colors (white→near-black)')
  }
  if (report.face.transform !== 'none') {
    out.push(`face has transform (GPU layer): ${clip(report.face.transform, 60)}`)
  }
  if (report.reducedMotion) {
    out.push('prefers-reduced-motion: shine is frozen, wash still blends')
  }
  out.push(...noteAncestorEffects(report))
  if (out.length === 0 && wash?.mixBlendMode !== 'normal') {
    out.push(
      'computed blend looks set — if gold is still weak, GPU compositing',
    )
  }
  return out
}

export function snapshotCardArt(
  root: HTMLElement,
  meta: FoilDebugMeta,
): FoilDebugReport {
  const img = root.querySelector('.card-art-img') as HTMLImageElement | null
  const face = root.querySelector('.card-art-face')
  const src = img?.getAttribute('src') || ''
  let protocol = ''
  try {
    protocol = src ? new URL(src, location.origin).protocol.replace(':', '') : ''
  } catch {
    protocol = src.slice(0, 8)
  }

  const faceStyle = face ? getComputedStyle(face) : null
  const rootStyle = getComputedStyle(root)
  const ancestors: FoilAncestorSnap[] = []
  let node: Element | null = root.parentElement
  for (let i = 0; i < 10 && node && node !== document.documentElement; i += 1) {
    ancestors.push(snapAncestor(node))
    node = node.parentElement
  }

  const sheets = [...document.styleSheets]
  const cssHrefs = sheets
    .map((sheet) => sheet.href)
    .filter((href): href is string => Boolean(href))

  const faceSnap = snapLayer(face) || {
    mixBlendMode: 'n/a',
    opacity: 'n/a',
    filter: 'n/a',
    isolation: 'n/a',
    transform: 'n/a',
    backgroundImage: 'n/a',
    boxShadow: 'n/a',
  }

  const draft: Omit<FoilDebugReport, 'killers'> = {
    at: new Date().toISOString(),
    origin: location.origin,
    href: location.href,
    mode: import.meta.env.MODE,
    prod: import.meta.env.PROD,
    dpr: window.devicePixelRatio,
    colorScheme: matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light',
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    supportsPlusLighter: CSS.supports('mix-blend-mode', 'plus-lighter'),
    supportsLighten: CSS.supports('mix-blend-mode', 'lighten'),
    foilProp: meta.foilProp,
    foilResolved: meta.foilResolved,
    foilReady: meta.foilReady,
    alt: meta.alt,
    rootClass: root.className,
    imgSrc: src,
    imgCurrentSrc: img?.currentSrc || '',
    imgProtocol: protocol,
    imgNatural: img ? `${img.naturalWidth}x${img.naturalHeight}` : 'n/a',
    imgComplete: Boolean(img?.complete),
    layers: {
      wash: snapLayer(root.querySelector('.foil-wash')),
      shine: snapLayer(root.querySelector('.foil-shine')),
      fx: snapLayer(root.querySelector('.foil-fx')),
      fx2: snapLayer(root.querySelector('.foil-fx2')),
    },
    face: {
      ...faceSnap,
      overflow: faceStyle
        ? `${faceStyle.overflowX}/${faceStyle.overflowY}`
        : 'n/a',
      transformStyle: faceStyle?.transformStyle || 'n/a',
      isolation: faceStyle?.isolation || faceSnap.isolation,
    },
    root: {
      boxShadow: clip(rootStyle.boxShadow, 120),
      filter: rootStyle.filter,
      transform: rootStyle.transform,
    },
    ancestors,
    cssHrefs,
    styleTagCount: document.querySelectorAll('style').length,
    autoDark: detectAutoDark(),
    darkReader: detectDarkReader(),
    htmlColorScheme: getComputedStyle(document.documentElement).colorScheme,
    metaColorScheme:
      document.querySelector('meta[name="color-scheme"]')
        ?.getAttribute('content') || '(none)',
  }

  return { ...draft, killers: collectKillers(draft) }
}
